import { DateTime as LuxonDateTime, Settings } from "luxon";
import moment, { utc } from "moment";
import { EMPTY_STRING } from "src/components/constant/constant";

export const getTimeZone = () => {
  const userTimeZone = Settings.defaultZone;

  return userTimeZone;
};

export const dateTimeFormat = (data: string | number | null | undefined) => {
  if (
    data === null ||
    data === undefined ||
    data === EMPTY_STRING ||
    (typeof data === "string" && data.trim() === EMPTY_STRING)
  ) {
    return {
      date: "---",
      time: "---",
      datetime: "---",
      dwml: "---",
      dateInput: "---"
    };
  }

  const formattedData =
    typeof data === "string" && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(data)
      ? data.replace(" ", "T") + "Z"
      : data;

  const DateTime = utc(formattedData).local();

  if (!DateTime.isValid()) {
    return {
      date: "---",
      time: "---",
      datetime: "---",
      dwml: "---",
      dateInput: "---"
    };
  }

  return {
    date: DateTime.format("DD/MM/YYYY"),
    time: DateTime.format("hh:mm A"),
    datetime: DateTime.format("DD/MM/YYYY hh:mm A"),
    dwml: DateTime.format("DD MMM YYYY"),
    dateInput: DateTime.format("YYYY-MM-DD")
  };
};

export const normaliseEpochToIso = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return EMPTY_STRING;

  const text = String(value).trim();

  if (!/^\d+$/.test(text)) return text;

  const date = new Date(Number(text));

  return Number.isNaN(date.valueOf()) ? text : date.toISOString();
};

export const formatTimestamp = (timestamp: string | undefined): string | undefined => {
  if (timestamp && typeof timestamp === "string") {
    return timestamp.replace(" ", "T") + "Z";
  }
  return undefined;
};

export const getResetLimitDayValue = (weekday: number): { day: string; date: string } => {
  const today = moment();
  const todayWeekday = today.day();

  const targetDate = today.clone().day(weekday);

  if (weekday > todayWeekday) {
    targetDate.subtract(7, "days");
  }

  return {
    day: targetDate.date().toString(),
    date: targetDate.format("D MMM YYYY")
  };
};

// Australia/Sydney calendar-day <-> UTC ISO conversions (DST-aware via luxon).
// Backend stores pure UTC ISO; the dates users pick are Sydney calendar days.
export const SYDNEY_TIME_ZONE = "Australia/Sydney";

// "2026-08-25" -> "2026-08-24T14:00:00.000Z" (start of that Sydney day).
export const sydneyDayStartUtcIso = (day: string): string | undefined => {
  const dt = LuxonDateTime.fromISO(day, { zone: SYDNEY_TIME_ZONE });

  return dt.isValid ? (dt.startOf("day").toUTC().toISO() ?? undefined) : undefined;
};

// "2026-08-25" -> "2026-08-25T13:59:59.999Z" (end of that Sydney day, inclusive).
export const sydneyDayEndUtcIso = (day: string): string | undefined => {
  const dt = LuxonDateTime.fromISO(day, { zone: SYDNEY_TIME_ZONE });

  return dt.isValid ? (dt.endOf("day").toUTC().toISO() ?? undefined) : undefined;
};

// UTC ISO instant -> the Sydney calendar day containing it ("YYYY-MM-DD").
export const utcIsoToSydneyDay = (iso?: string | null): string => {
  if (!iso) return EMPTY_STRING;
  const dt = LuxonDateTime.fromISO(iso, { zone: SYDNEY_TIME_ZONE });

  return dt.isValid ? (dt.toISODate() ?? EMPTY_STRING) : EMPTY_STRING;
};

// Today as an Australia/Sydney calendar day ("YYYY-MM-DD").
export const todaySydneyDay = (): string =>
  LuxonDateTime.now().setZone(SYDNEY_TIME_ZONE).toISODate() ?? EMPTY_STRING;

// UTC ISO instant -> "25 Aug 2026" (the Sydney calendar-day label).
export const formatSydneyDay = (iso?: string | null): string => {
  if (!iso) return EMPTY_STRING;
  const dt = LuxonDateTime.fromISO(iso, { zone: SYDNEY_TIME_ZONE });

  return dt.isValid ? dt.toFormat("dd MMM yyyy") : iso;
};
